import {
  Box,
  CircularProgress,
  CircularProgressLabel,
  Flex,
  Progress,
  Text,
  Grid,
  GridItem,
  Icon,
  useColorModeValue,
  keyframes,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import React from "react";
import {AiFillThunderbolt} from "react-icons/ai";
import {FaBatteryThreeQuarters} from "react-icons/fa";

// Glass morphism styled components
const GlassMorphismBox = styled(Box)`
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.7);
  border-radius: 16px;
  padding: 1.5rem;
  transition: all 0.3s ease;
`;

const StyledProgress = styled(Progress)`
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2));
    z-index: 1;
  }
`;

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const StyledCircularProgress = styled(CircularProgress)`
  /* Removed drop shadow */
`;

const SessionLabel = styled(Text)`
  font-weight: 600;
  letter-spacing: 1px;
  position: relative;
  display: inline-block;
  padding: 0 10px;
  margin-bottom: 12px;
  
  &:before {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #23A979, transparent);
  }
`;
const ChargingSessionCard = ({session}) => {
  // Theme colors
  const labelColor = useColorModeValue("gray.700", "gray.200");
  const iconColor = useColorModeValue("#23A979", "#ff9966");
  const bgGradient = useColorModeValue(
    "linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)",
    "linear-gradient(135deg, rgba(45, 55, 72, 0.8) 0%, rgba(45, 55, 72, 0.6) 100%)"
  );
  
  // Calculate SOC color based on percentage
  const getSocColor = (soc) => {
    const percentage = Math.floor(soc) || 0;
    if (percentage >= 70) return "green.400";
    if (percentage >= 30) return "brand.500";
    return "red.400";
  };
  
  const socColor = getSocColor(session?.ConsumerSoc);
  
  return (
    <Box 
      w="100%" 
      minW={{
        base: "250px",
        md: "350px"
      }}
    >
      <Grid 
        templateColumns={{
          base: "1fr",
          md: "repeat(2, 1fr)"
        }} 
        gap={6}
      >
        {/* Consumption Section */}
        <GridItem>
          <GlassMorphismBox>
            <Flex direction="column" alignItems="center">
              <Flex 
                align="center" 
                justify="center" 
                mb={3}
              >
                <Icon 
                  as={AiFillThunderbolt} 
                  color={iconColor} 
                  boxSize={5} 
                  mr={2} 
                />
                <SessionLabel color={labelColor}>
                  CONSUMPTION
                </SessionLabel>
              </Flex>
              
              <Box position="relative" width="100%" mb={2}>
                <StyledProgress
                  isIndeterminate
                  height="40px"
                  width="100%"
                  borderRadius="12px"
                  mb={2}
                  colorScheme="brand"
                  background="rgba(255, 255, 255, 0.2)"
                  sx={{
                    "& > div": {
                      background: `linear-gradient(90deg, #23A979, #ff9966)`
                    }
                  }}
                />
                
                <Flex 
                  position="absolute" 
                  top="0" 
                  left="0" 
                  width="100%" 
                  height="100%" 
                  align="center" 
                  justify="center"
                  pointerEvents="none"
                >
                  <Text 
                    fontSize="lg" 
                    fontWeight="700" 
                    color="white" 
                    textShadow="0 1px 2px rgba(0,0,0,0.3)"
                  >
                    {session?.UnitsConsumed?.toFixed(2) || "0.00"} KwH
                  </Text>
                </Flex>
              </Box>
              
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Energy consumed during this session
              </Text>
            </Flex>
          </GlassMorphismBox>
        </GridItem>
        
        {/* SOC Section */}
        <GridItem>
          <GlassMorphismBox>
            <Flex direction="column" alignItems="center">
              <Flex 
                align="center" 
                justify="center" 
                mb={4}
              >
                <Icon 
                  as={FaBatteryThreeQuarters} 
                  color={iconColor} 
                  boxSize={5} 
                  mr={2} 
                />
                <SessionLabel color={labelColor}>
                  BATTERY SOC
                </SessionLabel>
              </Flex>
              
              <Box position="relative">
                <StyledCircularProgress
                  value={Math.floor(session?.ConsumerSoc) || 0}
                  color={socColor}
                  thickness="12px"
                  size="100px"
                  isIndeterminate={!session?.ConsumerSoc}
                  capIsRound
                  trackColor="rgba(255, 255, 255, 0.2)"
                >
                  <CircularProgressLabel 
                    fontSize="xl" 
                    fontWeight="700"
                    color={labelColor}
                  >
                    {Math.floor(session?.ConsumerSoc) || 0}%
                  </CircularProgressLabel>
                </StyledCircularProgress>
              </Box>
              
              <Text fontSize="sm" color="gray.500" mt={2} textAlign="center">
                Current battery charge level
              </Text>
            </Flex>
          </GlassMorphismBox>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default ChargingSessionCard;
